import {
  Box,
  FormControl,
  HStack,
  SimpleGrid,
  useBreakpointValue,
  Text,
  VStack,
  Button,
  Flex,
  Center,
} from '@chakra-ui/react';
import Image from 'next/image';
import { Field, Form } from 'react-final-form';
import SwiperClass from 'swiper/types/swiper-class';
import { useEffect, useState, useRef } from 'react';
import {
  InPageSurvey,
  SurveyQuestion,
  InPageSurveyFormValues,
  SurveyChoice,
} from '../hooks/useInPageSurvey';
import { inPageSurveyQuestionType } from '../common/libs/inPageSurveyQuestionType';

/**
 * アンケートページ（インページサーベイ用；グリッド版）
 *
 *   1ページにつき1問を扱う
 *   注）サーベイページ表示時のGAイベント送信処理は親コンポーネント側で（Swiperのページめくりをトリガーに）行う
 *      現状、設問の形式は複数選択式のみ対応
 */
export default function SurveyPageGrid({
  survey,
  question,
  pageIndex,
  swiper,
  onSelectionChange,
}: {
  survey: InPageSurvey;
  question: SurveyQuestion;
  pageIndex: number;
  swiper: SwiperClass | null;
  onSelectionChange: (selected: number[]) => void;
}) {
  const [selectingsChoiceIds, setSelectingsChoiceIds] = useState<number[]>([]);
  const choiceColumnNum = Math.floor((question?.choices?.length || 0) / 4) || 1;

  const initialFormValues: InPageSurveyFormValues = {
    surveyId: survey.surveyId,
    questionNumber: question.questionNumber,
    answer: null,
  };

  // ＜スワイプ閾値の動的変更用＞
  // 選択肢ボタンをタップした際、ポインタの移動を検出してスワイプが発動してしまう事象が発生した。
  // 複数選択型、かつ回答ボタンなしの設問では、スワイプの誤発動により回答が確定してしまうため、
  // スワイプの閾値を引き上げることで対処した。
  // アクティブなサーベイページが表示されている間のみ Swiper の threshold を引き上げる。
  // 既存ページ遷移 UX を損なわないよう、非表示時には元の値へ戻す。
  const originalThresholdRef = useRef<number | null>(null);
  const RAISED_THRESHOLD = 28; // 誤タップで 10〜15px 程度動く想定より十分高めに設定

  useEffect(() => {
    if (!swiper) return;
    const isVisible = swiper.activeIndex === pageIndex;

    if (isVisible) {
      // ページ表示時に閾値を引き上げ
      if (originalThresholdRef.current === null) {
        originalThresholdRef.current = (swiper.params as any).threshold ?? 0;
      }
      if ((swiper.params as any).threshold !== RAISED_THRESHOLD) {
        (swiper.params as any).threshold = RAISED_THRESHOLD;
        swiper.update();
      }
    } else {
      // ページ表示終了時に元の値に戻す
      if (originalThresholdRef.current !== null) {
        (swiper.params as any).threshold = originalThresholdRef.current;
        swiper.update();
        originalThresholdRef.current = null;
      }
    }
  }, [swiper?.activeIndex, pageIndex, swiper]);

  type SurveyChoiceWithSelected = SurveyChoice & { selected: boolean };
  const choicesForView = question?.choices?.map((choice: SurveyChoice) => ({
    ...choice,
    selected: selectingsChoiceIds.includes(choice.choiceNumber),
  })) as SurveyChoiceWithSelected[];

  const onClickChoice = (choiceNumber: number) => {
    setSelectingsChoiceIds((prev) => {
      const next = prev.includes(choiceNumber)
        ? prev.filter((v) => v !== choiceNumber)
        : [...prev, choiceNumber];
      onSelectionChange(next);
      return next;
    });
  };

  type OptionButtonProps = {
    caption: string;
    selected: boolean;
    onClick: () => void;
  };
  const OptionButton: React.FC<OptionButtonProps> = ({
    caption,
    selected,
    onClick,
  }) => {
    const captionLabel = useBreakpointValue([
      <Text
        key={`choice-single-line`}
        fontSize={'12px'}
        fontWeight="550"
        color={selected ? 'gray.100' : 'gray.900'}
        textAlign="center"
      >
        {caption.replace(/\r?\n/g, '')}
      </Text>,
      caption?.split(/\r?\n/).map((line: string, i: number) => (
        <Text
          key={`choice-line-${i}`}
          fontSize={['12px', '14px', 'medium', 'medium', '17px', '2xl', '2xl']}
          fontWeight="550"
          color={selected ? 'gray.100' : 'gray.900'}
          textAlign="center"
        >
          {line}
          <br />
        </Text>
      )),
    ]);

    return (
      <Button
        type="button"
        height={'100%'}
        width={'100%'}
        onClick={onClick}
        padding={'2px 8px'}
        borderRadius={['8px', '10px']}
        backgroundColor={selected ? 'gray.500' : '#F4F4F4'}
        wordBreak={'break-word'}
        whiteSpace={'normal'}
      >
        <VStack spacing={0}>{captionLabel}</VStack>
      </Button>
    );
  };

  const questionLabel = useBreakpointValue([
    <Text
      key={'question-single-line'}
      fontSize={'14px'}
      fontWeight="990"
      color="textPrimary"
      textAlign="center"
    >
      Ｑ．{question.question.replace(/\r?\n/g, '')}
    </Text>,
    question.question?.split(/\r?\n/).map((line: any, i: number) => (
      <Text
        key={`question-line-${i}`}
        fontSize={['14px', 'large', 'large', 'xl', 'xl', '3xl']}
        fontWeight="990"
        color="textPrimary"
        textAlign="center"
      >
        {`${i === 0 ? 'Ｑ．' : ''}${line}`}
        <br />
      </Text>
    )),
  ]);

  const logoImgHight = useBreakpointValue([20, 40]);
  const logoImgWidth = useBreakpointValue([65, 130]);

  return (
    <Box height={'100%'} width={'100%'} bgColor={'white'}>
      <Flex
        direction="column"
        height={'100%'}
        width={'100%'}
        py={[1, 2]}
        justifyContent={'space-between'}
        gap={[1, 2]}
      >
        <VStack px={[1, 4]} spacing={0} width={'100%'}>
          {questionLabel}
        </VStack>

        <Center
          flex={1}
          justifyContent={'center'}
          h={'100%'}
          w={'100%'}
          py={[0, 1]}
        >
          <Form
            style={{ height: '100%' }}
            initialValues={initialFormValues}
            onSubmit={() => {}}
            render={() => {
              return (
                <Box alignContent="center" px={[1, 8]} h={'100%'} w={'100%'}>
                  {question.questionType ===
                    inPageSurveyQuestionType.MULTIPLE_CHOICE &&
                    question.choices && (
                      <Field<InPageSurveyFormValues['answer']> name="answer">
                        {({ input, meta }) => (
                          <FormControl
                            id="answer"
                            isInvalid={meta.touched && meta.error}
                            key={`survey-${survey.surveyId}-q${question.questionNumber}-choices`}
                          >
                            <SimpleGrid
                              columns={choiceColumnNum}
                              spacing={[1, 1.5]}
                              h={'100%'}
                            >
                              {choicesForView?.map(
                                (choice: SurveyChoiceWithSelected) => (
                                  <OptionButton
                                    key={`survey-${survey.surveyId}-q${question.questionNumber}-c${choice.choiceNumber}`}
                                    caption={choice.choiceName}
                                    selected={choice.selected}
                                    onClick={() => {
                                      onClickChoice(choice.choiceNumber);
                                      input.onBlur();
                                    }}
                                  />
                                ),
                              )}
                            </SimpleGrid>
                          </FormControl>
                        )}
                      </Field>
                    )}
                  {/* TODO: INPUTおよびSINGLE_CHOICEは未対応（将来的に実装予定） */}
                </Box>
              );
            }}
          />
        </Center>

        <HStack
          alignItems={'end'}
          w="100%"
          h={['8%', '10%']}
          maxH={['8%', '10%']}
          justifyContent={'space-between'}
          px={1}
        >
          {!!survey.logoImgUrl && (
            <Image
              src={survey.logoImgUrl}
              alt="logo"
              objectFit="contain"
              height={logoImgHight}
              width={logoImgWidth}
            />
          )}
        </HStack>
      </Flex>
    </Box>
  );
}
